/**
 * REC-CONF-2: Extract PO Exception from Receipt Notes
 *
 * Parses the structured "Exception: ..." line from receiptNotes
 * and returns the normalized exception enum value, or null.
 *
 * This is the single source of truth for exception extraction.
 * Do not duplicate this logic elsewhere.
 */

import type { POExceptionType } from './formatPOException';

/** Map of human-readable exception labels back to enum values */
const LABEL_TO_ENUM: Record<string, POExceptionType> = {
  'Damaged freight': 'damaged',
  'Refused at dock': 'refused',
  'Missing freight': 'missing',
  'Carrier issue': 'carrier_issue',
  'Supplier issue': 'supplier_issue',
  'Mixed condition': 'mixed_condition',
};

/**
 * Extract exception type from receiptNotes.
 *
 * @param receiptNotes - The raw receipt notes string
 * @returns The normalized exception enum value, or null if no exception
 */
export function extractPOExceptionFromReceiptNotes(
  receiptNotes?: string | null
): POExceptionType | null {
  if (!receiptNotes) return null;
  const match = receiptNotes.match(/Exception: (.+)$/m);
  if (!match) return null;
  const label = match[1].trim();
  return LABEL_TO_ENUM[label] ?? null;
}

/**
 * Strip the "Exception: ..." line from notes, returning clean user notes.
 */
export function stripExceptionFromNotes(notes: string): string {
  return notes.replace(/\n?Exception: .+$/m, '').trim();
}
